import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Video as VideoIcon, VideoOff, MonitorUp, PhoneOff, MessageSquare, Send, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const WS_BASE = BACKEND.replace(/^http/, "ws");
const RTC_CFG = { iceServers: [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }] };

const RemoteVideo = ({ streamsRef, id, label }) => (
  <div className="relative rounded-xl overflow-hidden bg-black aspect-video" data-testid={`remote-video-${id}`}>
    <video autoPlay playsInline
      ref={(el) => { if (el && streamsRef.current[id] && el.srcObject !== streamsRef.current[id]) el.srcObject = streamsRef.current[id]; }}
      className="w-full h-full object-cover" />
    <span className="absolute bottom-1.5 left-2 text-[11px] font-bold text-white bg-black/50 rounded px-1.5 py-0.5">{label}</span>
  </div>
);

export default function WebRTCCall({ token, name, hostKey = "", isHost = false, onEnded }) {
  const [status, setStatus] = useState("connecting");
  const [peers, setPeers] = useState([]);
  const [knocks, setKnocks] = useState([]);
  const [chat, setChat] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatText, setChatText] = useState("");
  const [unread, setUnread] = useState(0);
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [mediaErr, setMediaErr] = useState("");
  const [connErr, setConnErr] = useState("");
  const [, force] = useState(0);
  const wsRef = useRef(null);
  const myIdRef = useRef("");
  const pcsRef = useRef({});
  const streamsRef = useRef({});
  const localStreamRef = useRef(null);
  const screenRef = useRef(null);
  const localVideoRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatOpenRef = useRef(false);
  chatOpenRef.current = chatOpen;

  const send = (msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) ws.send(JSON.stringify(msg));
  };
  const sendSignal = (to, data) => send({ type: "signal", to, data });

  const attachPeer = (id) => {
    if (pcsRef.current[id]) return pcsRef.current[id];
    const pc = new RTCPeerConnection(RTC_CFG);
    const st = { pc, makingOffer: false, polite: myIdRef.current > id };
    pcsRef.current[id] = st;
    (localStreamRef.current?.getTracks() || []).forEach((t) => pc.addTrack(t, localStreamRef.current));
    pc.onicecandidate = (e) => { if (e.candidate) sendSignal(id, { candidate: e.candidate.toJSON() }); };
    pc.ontrack = (e) => { streamsRef.current[id] = e.streams[0]; force((x) => x + 1); };
    pc.onconnectionstatechange = () => { if (pc.connectionState === "failed") pc.restartIce(); force((x) => x + 1); };
    pc.onnegotiationneeded = async () => {
      try {
        st.makingOffer = true;
        await pc.setLocalDescription();
        sendSignal(id, { description: pc.localDescription });
      } catch { /* ignore */ } finally { st.makingOffer = false; }
    };
    return st;
  };

  const handleSignal = async (from, data) => {
    const st = attachPeer(from);
    const pc = st.pc;
    try {
      if (data.description) {
        const desc = data.description;
        const collision = desc.type === "offer" && (st.makingOffer || pc.signalingState !== "stable");
        if (collision && !st.polite) return;
        if (collision) await pc.setLocalDescription({ type: "rollback" });
        await pc.setRemoteDescription(desc);
        if (desc.type === "offer") {
          await pc.setLocalDescription();
          sendSignal(from, { description: pc.localDescription });
        }
      } else if (data.candidate) {
        await pc.addIceCandidate(data.candidate).catch(() => {});
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        localStreamRef.current = stream;
        if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      } catch {
        setMediaErr("Camera/mic unavailable — you'll join with chat and file sharing only.");
      }
      if (dead) return;
      const ws = new WebSocket(`${WS_BASE}/api/ws/call/${token}?name=${encodeURIComponent(name)}&host_key=${encodeURIComponent(hostKey)}`);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        const msg = JSON.parse(ev.data);
        if (msg.type === "joined" || msg.type === "admitted") {
          myIdRef.current = msg.id || myIdRef.current;
          (msg.peers || []).forEach((p) => attachPeer(p.id));
          setPeers(msg.peers || []);
          setStatus("live");
        } else if (msg.type === "waiting") {
          setStatus("waiting");
        } else if (msg.type === "rejected") {
          setStatus("rejected");
        } else if (msg.type === "peer-joined") {
          setPeers((old) => (old.some((x) => x.id === msg.id) ? old : [...old, { id: msg.id, name: msg.name, host: msg.host }]));
          attachPeer(msg.id);
        } else if (msg.type === "peer-left") {
          const st = pcsRef.current[msg.id];
          if (st) { st.pc.close(); delete pcsRef.current[msg.id]; }
          delete streamsRef.current[msg.id];
          setPeers((old) => old.filter((x) => x.id !== msg.id));
        } else if (msg.type === "knock") {
          setKnocks((old) => (old.some((x) => x.id === msg.id) ? old : [...old, { id: msg.id, name: msg.name }]));
        } else if (msg.type === "signal") {
          handleSignal(msg.from, msg.data || {});
        } else if (msg.type === "chat") {
          setChat((old) => [...old.slice(-99), msg]);
          if (!chatOpenRef.current) setUnread((u) => u + 1);
        }
      };
      ws.onerror = () => setConnErr("Connection problem — check your internet and rejoin.");
      ws.onclose = (e) => { if (e.code === 4404) setConnErr("This call link isn't valid anymore."); };
    })();
    return () => {
      dead = true;
      Object.values(pcsRef.current).forEach(({ pc }) => pc.close());
      pcsRef.current = {};
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      screenRef.current?.stop();
      wsRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat, chatOpen]);

  const toggleMute = () => { localStreamRef.current?.getAudioTracks().forEach((t) => { t.enabled = muted; }); setMuted(!muted); };
  const toggleCam = () => { localStreamRef.current?.getVideoTracks().forEach((t) => { t.enabled = camOff; }); setCamOff(!camOff); };

  const stopShare = () => {
    const camTrack = localStreamRef.current?.getVideoTracks()[0] || null;
    Object.values(pcsRef.current).forEach(({ pc }) => {
      const sender = pc.getSenders().find((s) => s.track?.kind === "video");
      if (sender) sender.replaceTrack(camTrack);
    });
    if (localVideoRef.current && localStreamRef.current) localVideoRef.current.srcObject = localStreamRef.current;
    setSharing(false);
  };
  const shareScreen = async () => {
    if (sharing) { stopShare(); return; }
    try {
      const disp = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const track = disp.getVideoTracks()[0];
      screenRef.current = track;
      Object.values(pcsRef.current).forEach(({ pc }) => {
        const sender = pc.getSenders().find((s) => s.track?.kind === "video");
        if (sender) sender.replaceTrack(track);
        else if (localStreamRef.current) pc.addTrack(track, localStreamRef.current);
      });
      if (localVideoRef.current) localVideoRef.current.srcObject = disp;
      track.onended = stopShare;
      setSharing(true);
    } catch { /* user cancelled */ }
  };

  const hangUp = () => {
    Object.values(pcsRef.current).forEach(({ pc }) => pc.close());
    pcsRef.current = {};
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    wsRef.current?.close();
    onEnded?.();
  };

  const sendChat = () => {
    const text = chatText.trim();
    if (!text) return;
    send({ type: "chat", text });
    setChatText("");
  };

  const ctlBtn = "h-11 w-11 rounded-full grid place-items-center transition-colors";
  const ctlOn = "bg-white/15 text-white hover:bg-white/25";
  const ctlOff = "bg-[#D5281B] text-white hover:bg-[#b01f14]";

  if (status === "waiting") {
    return (
      <div className="rounded-2xl border border-slate-700 bg-[#0F141B] p-8 text-center" data-testid="call-waiting">
        <Loader2 className="h-8 w-8 text-[#00D2C2] animate-spin mx-auto mb-3" />
        <p className="text-white font-bold">You're in the waiting room</p>
        <p className="text-sm text-slate-400 mt-1">The host has been notified — they'll let you in shortly.</p>
      </div>
    );
  }
  if (status === "rejected") {
    return (
      <div className="rounded-2xl border border-slate-700 bg-[#0F141B] p-8 text-center" data-testid="call-rejected">
        <p className="text-white font-bold">The host couldn't let you in this time</p>
        <p className="text-sm text-slate-400 mt-1">Check the time of your call, or contact Wales Event Medical Services.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-700 bg-[#0F141B] p-3 sm:p-4" data-testid="webrtc-call">
      {status === "connecting" && (
        <p className="text-xs text-slate-400 mb-2 flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Connecting…</p>
      )}
      {connErr && <p className="text-xs text-red-400 mb-2" data-testid="call-conn-error">{connErr}</p>}
      {mediaErr && <p className="text-xs text-amber-400 mb-2" data-testid="call-media-note">{mediaErr}</p>}

      {isHost && knocks.map((k) => (
        <div key={k.id} className="mb-2 flex items-center gap-2 bg-amber-50 rounded-xl border border-amber-200 p-3" data-testid={`knock-${k.id}`}>
          <span className="mr-auto text-sm font-semibold text-[#0F2547]">{k.name} is knocking…</span>
          <button onClick={() => { send({ type: "admit", id: k.id }); setKnocks((old) => old.filter((x) => x.id !== k.id)); }}
            className="h-9 px-4 rounded-lg bg-[#007F3B] hover:bg-[#006831] text-white text-xs font-bold transition-colors" data-testid={`knock-admit-${k.id}`}>Let in</button>
          <button onClick={() => { send({ type: "reject", id: k.id }); setKnocks((old) => old.filter((x) => x.id !== k.id)); }}
            className="h-9 px-3 rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 text-xs font-bold transition-colors" data-testid={`knock-reject-${k.id}`}>Turn away</button>
        </div>
      ))}

      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${peers.length > 1 ? "240px" : "320px"}, 1fr))` }}>
        <div className="relative rounded-xl overflow-hidden bg-black aspect-video" data-testid="local-video">
          <video autoPlay playsInline muted ref={localVideoRef} className="w-full h-full object-cover" />
          {camOff && <div className="absolute inset-0 grid place-items-center bg-slate-800"><VideoOff className="h-8 w-8 text-slate-500" /></div>}
          <span className="absolute bottom-1.5 left-2 text-[11px] font-bold text-white bg-black/50 rounded px-1.5 py-0.5">You{isHost ? " (Host)" : ""}</span>
        </div>
        {peers.map((p) => <RemoteVideo key={p.id} streamsRef={streamsRef} id={p.id} label={p.name} />)}
        {status === "live" && peers.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-600 aspect-video grid place-items-center" data-testid="call-alone">
            <p className="text-xs text-slate-400 px-4 text-center">You're the only one here — others join from the call link{isHost ? " and you'll let them in" : ""}.</p>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center justify-center gap-2.5 flex-wrap">
        <button onClick={toggleMute} aria-label="Toggle microphone" className={`${ctlBtn} ${muted ? ctlOff : ctlOn}`} data-testid="call-mute-btn">
          {muted ? <MicOff className="h-4.5 w-4.5" /> : <Mic className="h-4.5 w-4.5" />}
        </button>
        <button onClick={toggleCam} aria-label="Toggle camera" className={`${ctlBtn} ${camOff ? ctlOff : ctlOn}`} data-testid="call-cam-btn">
          {camOff ? <VideoOff className="h-4.5 w-4.5" /> : <VideoIcon className="h-4.5 w-4.5" />}
        </button>
        <button onClick={shareScreen} aria-label="Share screen" className={`${ctlBtn} ${sharing ? "bg-[#005EB8] text-white" : ctlOn}`} data-testid="call-share-btn">
          <MonitorUp className="h-4.5 w-4.5" />
        </button>
        <button onClick={() => { setChatOpen((v) => !v); setUnread(0); }} aria-label="Toggle chat" className={`${ctlBtn} ${ctlOn} relative`} data-testid="call-chat-btn">
          <MessageSquare className="h-4.5 w-4.5" />
          {unread > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-4 px-0.5 rounded-full bg-[#D5281B] text-white text-[9px] font-bold grid place-items-center">{unread}</span>}
        </button>
        <button onClick={hangUp} aria-label="Leave call" className="h-11 px-5 rounded-full bg-[#D5281B] hover:bg-[#b01f14] text-white text-xs font-extrabold inline-flex items-center gap-1.5 transition-colors" data-testid="call-hangup-btn">
          <PhoneOff className="h-4 w-4" /> Leave
        </button>
      </div>

      {chatOpen && (
        <div className="mt-3 rounded-xl border border-slate-700 bg-[#0B0F14] p-3" data-testid="call-chat">
          <div className="max-h-48 overflow-y-auto space-y-1.5 mb-2">
            {chat.length === 0 && <p className="text-[11px] text-slate-500">No messages yet — say hello.</p>}
            {chat.map((m, i) => (
              <p key={i} className="text-xs text-slate-200"><span className="font-bold text-[#00D2C2]">{m.name}: </span>{m.text}</p>
            ))}
            <div ref={chatEndRef} />
          </div>
          <div className="flex gap-2">
            <input value={chatText} onChange={(e) => setChatText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }}
              placeholder="Message…" className="flex-1 h-10 rounded-lg border border-slate-600 bg-[#121820] px-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#00D2C2]"
              data-testid="call-chat-input" />
            <button onClick={sendChat} aria-label="Send message" className="h-10 w-10 rounded-lg bg-[#00A499] hover:bg-[#008f85] text-white grid place-items-center transition-colors" data-testid="call-chat-send">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
